import React, { useMemo, useState, useEffect, useRef } from 'react';
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString, AsYouType } from 'libphonenumber-js';
import { formatPhoneFR, detectCountry } from '../../utils/formatFR';

/**
 * PhoneInput — Saisie téléphone internationale.
 *
 * Pattern : [drapeau ▼ +33] [6 12 34 56 78]
 *
 * Props :
 *  - value: string E.164 ou null (ex: "+33612345678")
 *  - defaultCountry: code ISO du pays par défaut (default: 'FR')
 *  - onChange: ({ e164, country, isValid }) => void
 *  - placeholder, disabled, testid, className
 *  - error: string (affiché en rouge sous le champ)
 *
 * Comportement :
 *  - Si `value` est E.164, on extrait pays + numéro local
 *  - Si `value` est un ancien numéro local sans indicatif → assume FR
 *  - L'utilisateur peut changer de pays via le dropdown
 *  - onChange est appelé à chaque modification avec la normalisation E.164
 */

// Drapeaux émoji par code pays (Unicode regional indicators)
const flag = (countryCode) => {
  if (!countryCode || countryCode.length !== 2) return '🌐';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((c) => 127397 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

// Noms des pays principaux (les autres restent en code ISO si non listés)
const COUNTRY_NAMES = {
  FR: 'France',
  BE: 'Belgique',
  CH: 'Suisse',
  LU: 'Luxembourg',
  MC: 'Monaco',
  ES: 'Espagne',
  PT: 'Portugal',
  IT: 'Italie',
  DE: 'Allemagne',
  NL: 'Pays-Bas',
  GB: 'Royaume-Uni',
  IE: 'Irlande',
  US: 'États-Unis',
  CA: 'Canada',
  MA: 'Maroc',
  DZ: 'Algérie',
  TN: 'Tunisie',
  SN: 'Sénégal',
  CI: "Côte d'Ivoire",
  CM: 'Cameroun',
  CD: 'RD Congo',
  GA: 'Gabon',
  RE: 'Réunion',
  GP: 'Guadeloupe',
  MQ: 'Martinique',
  GF: 'Guyane',
  YT: 'Mayotte',
  NC: 'Nouvelle-Calédonie',
  PF: 'Polynésie française',
  CN: 'Chine',
  JP: 'Japon',
  IN: 'Inde',
  RU: 'Russie',
  TR: 'Turquie',
  AE: 'Émirats arabes unis',
  SA: 'Arabie saoudite',
  IL: 'Israël',
  AU: 'Australie',
  NZ: 'Nouvelle-Zélande',
  BR: 'Brésil',
  AR: 'Argentine',
  MX: 'Mexique',
};

// Pays à mettre en tête de liste (les plus utilisés pour le CRM FR)
const PRIORITY = ['FR', 'BE', 'CH', 'LU', 'MC', 'ES', 'PT', 'IT', 'DE', 'NL', 'GB', 'US', 'MA', 'DZ', 'TN'];

const PhoneInput = ({
  value,
  defaultCountry = 'FR',
  onChange,
  placeholder = '6 12 34 56 78',
  disabled = false,
  testid,
  className = '',
  error = null,
}) => {
  // Construire la liste des pays UNE FOIS au mount
  const allCountries = useMemo(() => {
    try {
      const supported = getCountries();
      const priorityList = PRIORITY.filter((c) => supported.includes(c));
      const others = supported.filter((c) => !PRIORITY.includes(c)).sort();
      return [...priorityList, ...others].map((iso) => ({
        iso,
        code: getCountryCallingCode(iso),
        name: COUNTRY_NAMES[iso] || iso,
        flag: flag(iso),
      }));
    } catch {
      return [];
    }
  }, []);

  // Pays courant + numéro national
  const [country, setCountry] = useState(() => detectCountry(value, defaultCountry));
  const [national, setNational] = useState(() => {
    if (!value) return '';
    try {
      const phone = parsePhoneNumberFromString(String(value), defaultCountry);
      if (phone) return phone.formatNational().replace(/^0/, '').trim();
      return String(value).replace(/^\+?\d{1,4}/, '').trim();
    } catch {
      return String(value);
    }
  });
  const [showDropdown, setShowDropdown] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);

  // Sync depuis la prop si elle change (ex: reset formulaire)
  useEffect(() => {
    setCountry(detectCountry(value, defaultCountry));
    if (!value) {
      setNational('');
      return;
    }
    try {
      const phone = parsePhoneNumberFromString(String(value), defaultCountry);
      if (phone) {
        setNational(phone.formatNational().replace(/^0/, '').trim());
      }
    } catch {
      /* ignore */
    }
  }, [value]);

  // Fermer dropdown au clic extérieur
  useEffect(() => {
    if (!showDropdown) return;
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showDropdown]);

  // Propager le changement au parent (E.164 + validité)
  const emit = (nextNational, nextCountry) => {
    const e164 = (() => {
      if (!nextNational) return null;
      try {
        const phone = parsePhoneNumberFromString(nextNational, nextCountry);
        if (phone && phone.isValid()) return phone.number;
        // Si pas valide mais l'utilisateur tape → on stocke quand même
        // une approximation en concat (mais isValid:false → le parent peut refuser au save)
        return null;
      } catch {
        return null;
      }
    })();
    if (onChange) {
      onChange({
        e164,
        country: nextCountry,
        national: nextNational,
        isValid: !!e164,
      });
    }
  };

  const handleCountryChange = (iso) => {
    setCountry(iso);
    setShowDropdown(false);
    setSearch('');
    emit(national, iso);
  };

  const handleNumberChange = (e) => {
    // Formate au fur et à mesure pour l'affichage local
    const raw = e.target.value;
    const formatted = new AsYouType(country).input(raw);
    setNational(formatted);
    emit(formatted, country);
  };

  // Filtrage liste pays
  const filteredCountries = useMemo(() => {
    if (!search) return allCountries;
    const q = search.toLowerCase();
    return allCountries.filter(
      (c) => c.name.toLowerCase().includes(q) || c.code.includes(q) || c.iso.toLowerCase().includes(q)
    );
  }, [search, allCountries]);

  const currentCountry = allCountries.find((c) => c.iso === country) || allCountries[0];

  return (
    <div ref={containerRef} className={`relative ${className}`} data-testid={testid}>
      <div
        className={`flex items-center border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500/40 focus-within:border-blue-400 ${
          error ? 'border-rose-300' : 'border-slate-300'
        } ${disabled ? 'bg-slate-50 opacity-60' : 'bg-white'}`}
      >
        {/* Dropdown pays */}
        <button
          type="button"
          onClick={() => !disabled && setShowDropdown((v) => !v)}
          disabled={disabled}
          data-testid={testid ? `${testid}-country-btn` : undefined}
          className="flex items-center gap-1.5 px-2.5 py-2 border-r border-slate-200 hover:bg-slate-50 text-sm whitespace-nowrap"
        >
          <span className="text-base leading-none">{currentCountry?.flag || '🌐'}</span>
          <span className="text-slate-700 font-medium num">+{currentCountry?.code || '?'}</span>
          <i className="bi bi-chevron-down text-[10px] text-slate-400"></i>
        </button>

        {/* Input numéro local */}
        <input
          type="tel"
          value={national}
          onChange={handleNumberChange}
          placeholder={placeholder}
          disabled={disabled}
          data-testid={testid ? `${testid}-number-input` : undefined}
          autoComplete="tel-national"
          className="flex-1 px-3 py-2 text-sm focus:outline-none num bg-transparent"
        />
      </div>

      {error && <p className="text-xs text-rose-600 mt-1">{error}</p>}

      {/* Dropdown ouvert */}
      {showDropdown && !disabled && (
        <div className="absolute left-0 top-full mt-1 w-64 bg-white rounded-lg shadow-xl border border-slate-200 z-50">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un pays…"
            data-testid={testid ? `${testid}-country-search` : undefined}
            autoFocus
            className="w-full px-3 py-2 text-sm border-b border-slate-100 focus:outline-none"
          />
          <ul className="max-h-64 overflow-y-auto py-1">
            {filteredCountries.map((c) => (
              <li key={c.iso}>
                <button
                  type="button"
                  onClick={() => handleCountryChange(c.iso)}
                  data-testid={testid ? `${testid}-country-${c.iso}` : undefined}
                  className={`w-full text-left flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-blue-50 ${
                    c.iso === country ? 'bg-blue-50 font-semibold' : ''
                  }`}
                >
                  <span className="text-base leading-none w-5">{c.flag}</span>
                  <span className="flex-1 text-slate-700 truncate">{c.name}</span>
                  <span className="text-xs text-slate-400 num">+{c.code}</span>
                </button>
              </li>
            ))}
            {filteredCountries.length === 0 && (
              <li className="px-3 py-2 text-xs text-slate-400 italic">Aucun pays trouvé</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PhoneInput;

// ─── Exports pour tests / helpers ─────────────────────────────────────
export { detectCountry, formatPhoneFR as formatPhoneInternational };
